import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Bold, Italic, Underline, Trash2 } from 'lucide-react';

// Available fonts for text styling
const FONTS = [
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Times New Roman, serif', label: 'Times' },
  { value: 'Courier New, monospace', label: 'Courier' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Impact, sans-serif', label: 'Impact' },
  { value: 'Comic Sans MS, cursive', label: 'Comic Sans' },
];

// Color palette for text
const COLOR_PALETTE = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
  '#FF00FF', '#00FFFF', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
  '#F8B500', '#FF6F61', '#6B5B95', '#88B04B', '#F7CAC9', '#92A8D1',
];

function TextBox({
  layer,
  isSelected = false,
  onSelect = () => {},
  onUpdate = () => {},
  onDelete = () => {},
  onTransformStart = () => {}, // Called when drag/resize starts
  onTransformComplete = () => {}, // Called when drag/resize finishes
  containerBounds = null, // Now this is the IMAGE bounds (actual rendered image rect)
  parentContainerBounds = null, // The parent container bounds (for calculating offset)
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPos, setDragStartPos] = useState(null); // Track initial position for drag threshold
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  const textRef = useRef(null);
  const boxRef = useRef(null);
  const toolbarRef = useRef(null); // Reference to toolbar for blur detection
  const colorPickerRef = useRef(null);
  const wrapperRef = useRef(null);

  // Calculate offset from parent container to image (for object-fit: contain centering)
  const imageOffset = useMemo(() => {
    if (!containerBounds || !parentContainerBounds) return { x: 0, y: 0 };
    return {
      x: containerBounds.left - parentContainerBounds.left,
      y: containerBounds.top - parentContainerBounds.top,
    };
  }, [containerBounds, parentContainerBounds]);

  // Initialize and sync text content with layer
  useEffect(() => {
    if (textRef.current) {
      // Always sync content when not editing, or on initial render
      if (!layer.isEditing || textRef.current.textContent === '') {
        textRef.current.textContent = layer.content;
      }
    }
  }, [layer.content, layer.isEditing]);

  // Focus text input when entering edit mode
  useEffect(() => {
    if (layer.isEditing && textRef.current) {
      textRef.current.focus();
      // Place cursor at the end instead of selecting all (more natural behavior)
      const range = document.createRange();
      range.selectNodeContents(textRef.current);
      range.collapse(false); // Collapse to end
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [layer.isEditing]);

  // Handle mouse down for potential drag - only initiate drag after movement threshold
  const handleMouseDown = useCallback((e) => {
    if (layer.isEditing) return; // Don't drag while editing text
    e.stopPropagation();
    
    const box = boxRef.current;
    if (!box) return;
    
    const rect = box.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    // Store start position for drag threshold check
    setDragStartPos({ x: e.clientX, y: e.clientY });
    onSelect();
  }, [layer.isEditing, onSelect]);

  const handleMouseMove = useCallback((e) => {
    // Check if we should start dragging (only after moving beyond threshold)
    if (dragStartPos && !isDragging && !isResizing) {
      const dx = e.clientX - dragStartPos.x;
      const dy = e.clientY - dragStartPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Only start dragging after 5px movement threshold
      if (distance > 5) {
        setIsDragging(true);
        onTransformStart('text', 'move');
      } else {
        return; // Not enough movement yet
      }
    }
    
    if (!isDragging && !isResizing) return;
    
    if (isDragging && containerBounds) {
      const box = boxRef.current;
      const rect = box?.getBoundingClientRect();
      const boxWidth = rect?.width || 0;
      const boxHeight = rect?.height || 0;
      const boundsCenterX = containerBounds.width / 2;
      const boundsCenterY = containerBounds.height / 2;
      const snapThreshold = 6;

      let newX = e.clientX - containerBounds.left - dragOffset.x;
      let newY = e.clientY - containerBounds.top - dragOffset.y;

      const layerCenterX = newX + boxWidth / 2;
      const layerCenterY = newY + boxHeight / 2;

      if (Math.abs(layerCenterX - boundsCenterX) <= snapThreshold) {
        newX = boundsCenterX - boxWidth / 2;
      }

      if (Math.abs(layerCenterY - boundsCenterY) <= snapThreshold) {
        newY = boundsCenterY - boxHeight / 2;
      }

      const clampedX = Math.max(0, Math.min(newX, containerBounds.width - boxWidth));
      const clampedY = Math.max(0, Math.min(newY, containerBounds.height - boxHeight));

      onUpdate({
        ...layer,
        position: { x: clampedX, y: clampedY },
      });
    }
    
    if (isResizing && resizeHandle) {
      // Calculate new font size based on drag distance
      // Dragging down (positive movementY) should increase font size
      const deltaY = e.movementY;
      const newFontSize = Math.max(12, Math.min(120, layer.style.fontSize + deltaY));
      
      onUpdate({
        ...layer,
        style: {
          ...layer.style,
          fontSize: newFontSize,
        },
      });
    }
  }, [isDragging, isResizing, resizeHandle, containerBounds, dragOffset, dragStartPos, layer, onUpdate, onTransformStart]);

  const handleMouseUp = useCallback(() => {
    const wasDragging = isDragging;
    const wasResizing = isResizing;
    
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
    setDragStartPos(null); // Clear drag start position
    
    // Notify parent when transform completes
    if (wasDragging) {
      onTransformComplete('text', 'move');
    } else if (wasResizing) {
      onTransformComplete('text', 'resize');
    }
  }, [isDragging, isResizing, onTransformComplete]);

  // Attach global mouse events for dragging/resizing
  useEffect(() => {
    // Listen when drag might start (dragStartPos set) or when actually dragging/resizing
    if (dragStartPos || isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragStartPos, isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Handle resize handle mousedown
  const handleResizeMouseDown = useCallback((e, handle) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
  }, []);

  // Handle double click to edit
  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation();
    onUpdate({
      ...layer,
      isEditing: true,
    });
  }, [layer, onUpdate]);

  // Handle click outside to finish editing - more reliable than blur
  useEffect(() => {
    if (!layer.isEditing) return;

    const handleClickOutside = (e) => {
      // Check if click is inside the wrapper (textbox + toolbar)
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) {
        return; // Click inside, don't close
      }

      // Click outside - save and close editing
      const currentContent = textRef.current?.textContent || '';
      onUpdate({
        ...layer,
        content: currentContent,
        isEditing: false,
      });
      setShowColorPicker(false);
    };

    // Use mousedown to catch the click before focus changes
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [layer, onUpdate]);

  // Handle keydown for escape to cancel, enter to confirm
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      // Restore original content
      if (textRef.current) {
        textRef.current.textContent = layer.content;
      }
      onUpdate({
        ...layer,
        isEditing: false,
      });
      setShowColorPicker(false);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const currentContent = textRef.current?.textContent || '';
      onUpdate({
        ...layer,
        content: currentContent,
        isEditing: false,
      });
      setShowColorPicker(false);
    }
  }, [layer, onUpdate]);
  
  // Stop propagation on toolbar to prevent canvas click handlers
  const handleToolbarMouseDown = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Style toggle handlers
  const toggleBold = useCallback(() => {
    onUpdate({
      ...layer,
      style: {
        ...layer.style,
        fontWeight: layer.style.fontWeight === 'bold' ? 'normal' : 'bold',
      },
    });
  }, [layer, onUpdate]);

  const toggleItalic = useCallback(() => {
    onUpdate({
      ...layer,
      style: {
        ...layer.style,
        fontStyle: layer.style.fontStyle === 'italic' ? 'normal' : 'italic',
      },
    });
  }, [layer, onUpdate]);

  const toggleUnderline = useCallback(() => {
    onUpdate({
      ...layer,
      style: {
        ...layer.style,
        textDecoration: layer.style.textDecoration === 'underline' ? 'none' : 'underline',
      },
    });
  }, [layer, onUpdate]);

  const handleFontChange = useCallback((e) => {
    onUpdate({
      ...layer,
      style: {
        ...layer.style,
        fontFamily: e.target.value,
      },
    });
  }, [layer, onUpdate]);

  const handleFontSizeChange = useCallback((e) => {
    const size = parseInt(e.target.value, 10) || 24;
    onUpdate({
      ...layer,
      style: {
        ...layer.style,
        fontSize: Math.max(8, Math.min(200, size)),
      },
    });
  }, [layer, onUpdate]);

  const handleColorSelect = useCallback((color) => {
    onUpdate({
      ...layer,
      style: {
        ...layer.style,
        color: color,
      },
    });
    setShowColorPicker(false);
    // Refocus the text after color selection
    setTimeout(() => {
      textRef.current?.focus();
    }, 0);
  }, [layer, onUpdate]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!showColorPicker) return;
    
    const handleClickOutside = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  return (
    <div
      ref={wrapperRef}
      className="textbox-wrapper"
      style={{
        left: imageOffset.x + layer.position.x,
        top: imageOffset.y + layer.position.y,
      }}
    >
      {/* Floating toolbar - shows when selected and editing, positioned above the textbox */}
      {isSelected && layer.isEditing && (
        <div 
          ref={toolbarRef}
          className="textbox-toolbar"
          onMouseDown={handleToolbarMouseDown}
          onClick={(e) => {
            // Prevent clicks in the toolbar from bubbling to the canvas container,
            // which would otherwise dismiss editing mode.
            e.stopPropagation();
          }}
        >
          <select 
            className="textbox-font-select"
            value={layer.style.fontFamily}
            onChange={handleFontChange}
          >
            {FONTS.map((font) => (
              <option key={font.value} value={font.value}>
                {font.label}
              </option>
            ))}
          </select>
          
          <input
            type="number"
            className="textbox-size-input"
            value={layer.style.fontSize}
            onChange={handleFontSizeChange}
            min={8}
            max={200}
          />
          
          <div className="textbox-toolbar-divider" />
          
          <button
            className={`textbox-toolbar-btn ${layer.style.fontWeight === 'bold' ? 'active' : ''}`}
            onClick={toggleBold}
            title="Bold"
          >
            <Bold size={14} />
          </button>
          
          <button
            className={`textbox-toolbar-btn ${layer.style.fontStyle === 'italic' ? 'active' : ''}`}
            onClick={toggleItalic}
            title="Italic"
          >
            <Italic size={14} />
          </button>
          
          <button
            className={`textbox-toolbar-btn ${layer.style.textDecoration === 'underline' ? 'active' : ''}`}
            onClick={toggleUnderline}
            title="Underline"
          >
            <Underline size={14} />
          </button>
          
          <div className="textbox-toolbar-divider" />
          
          <div className="textbox-color-picker-container" ref={colorPickerRef}>
            <button
              className="textbox-toolbar-btn textbox-color-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPicker(!showColorPicker);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              title="Text color"
            >
              <div 
                className="textbox-color-preview"
                style={{ backgroundColor: layer.style.color }}
              />
            </button>
            
            {showColorPicker && (
              <div 
                className="textbox-color-dropdown"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {COLOR_PALETTE.map((color) => (
                  <button
                    key={color}
                    className="textbox-color-swatch"
                    style={{ backgroundColor: color }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColorSelect(color);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>
          
          <div className="textbox-toolbar-divider" />
          
          <button
            className="textbox-toolbar-btn textbox-delete-btn"
            onClick={() => onDelete(layer.id)}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}

      <div
        ref={boxRef}
        className={`textbox ${isSelected ? 'selected' : ''} ${layer.isEditing ? 'editing' : ''} ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onClick={(e) => {
          e.stopPropagation();
          if (!isSelected) onSelect();
        }}
      >
        <div
          ref={textRef}
          contentEditable={layer.isEditing}
          suppressContentEditableWarning
          className="textbox-content"
          style={{
            fontFamily: layer.style.fontFamily,
            fontSize: layer.style.fontSize,
            fontWeight: layer.style.fontWeight,
            fontStyle: layer.style.fontStyle,
            textDecoration: layer.style.textDecoration || 'none',
            color: layer.style.color,
          }}
          onKeyDown={handleKeyDown}
        />
        
        {/* Resize handles - only show when selected */}
        {isSelected && !layer.isEditing && (
          <>
            <div 
              className="textbox-resize-handle textbox-resize-se"
              onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
            />
          </>
        )}
      </div>

      <style jsx>{`
        .textbox-wrapper {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          z-index: 10;
        }

        .textbox {
          position: relative;
          cursor: move;
          padding: 4px 8px;
          border: 2px solid transparent;
          border-radius: 4px;
          user-select: none;
          white-space: pre-wrap;
          min-width: 20px;
          min-height: 20px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .textbox:hover {
          border-color: var(--color-border-input);
        }

        .textbox.selected {
          border-color: var(--color-text-secondary);
          box-shadow: 0 0 0 1px var(--color-border-input);
        }

        .textbox.editing {
          cursor: text;
          background: var(--color-bg-canvas-surface);
          border-color: var(--color-text-secondary);
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
        }

        .textbox.dragging {
          opacity: 0.8;
          cursor: grabbing;
        }

        .textbox-content {
          outline: none;
          min-width: 1px;
          direction: ltr;
          unicode-bidi: plaintext;
          text-align: left;
        }
        
        .textbox-content:empty::before {
          content: 'Type here...';
          color: var(--color-text-secondary);
          pointer-events: none;
        }

        .textbox-resize-handle {
          position: absolute;
          width: 10px;
          height: 10px;
          background: var(--color-bg-canvas-surface);
          border: 2px solid var(--color-text-secondary);
          border-radius: 2px;
        }

        .textbox-resize-se {
          right: -5px;
          bottom: -5px;
          cursor: nwse-resize;
        }

        /* Floating toolbar - positioned above textbox in flex column */
        .textbox-toolbar {
          position: relative;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px 8px;
          margin-bottom: 4px;
          background: var(--color-bg-canvas-surface);
          border: 1px solid var(--color-border-input);
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          user-select: none;
        }

        .textbox-font-select {
          padding: 4px 8px;
          border: 1px solid var(--color-border-input);
          border-radius: 4px;
          font-size: 12px;
          background: var(--color-bg-input);
          cursor: pointer;
          min-width: 80px;
          color: var(--color-text-primary);
        }

        .textbox-font-select:focus {
          outline: none;
          border-color: var(--color-text-secondary);
        }

        .textbox-size-input {
          width: 48px;
          padding: 4px 6px;
          border: 1px solid var(--color-border-input);
          border-radius: 4px;
          font-size: 12px;
          text-align: center;
          background: var(--color-bg-input);
          color: var(--color-text-primary);
        }

        .textbox-size-input:focus {
          outline: none;
          border-color: var(--color-text-secondary);
        }

        .textbox-toolbar-divider {
          width: 1px;
          height: 20px;
          background: var(--color-border-input);
          margin: 0 4px;
        }

        .textbox-toolbar-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          color: var(--color-text-secondary);
          transition: all 0.15s ease;
        }

        .textbox-toolbar-btn:hover {
          background: var(--color-bg-input);
          color: var(--color-text-primary);
        }

        .textbox-toolbar-btn.active {
          background: var(--color-bg-user-bubble);
          color: var(--color-text-primary);
        }

        .textbox-delete-btn:hover {
          background: rgba(220, 38, 38, 0.12);
          color: #dc2626;
        }

        .textbox-color-picker-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .textbox-color-preview {
          width: 16px;
          height: 16px;
          border-radius: 2px;
          border: 1px solid var(--color-border-input);
        }

        .textbox-color-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 4px;
          padding: 8px;
          background: var(--color-bg-canvas-surface);
          border: 1px solid var(--color-border-input);
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          z-index: 1001;
        }

        .textbox-color-swatch {
          width: 20px;
          height: 20px;
          padding: 0;
          border: 1px solid var(--color-border-input);
          border-radius: 3px;
          cursor: pointer;
          transition: transform 0.1s ease;
        }

        .textbox-color-swatch:hover {
          transform: scale(1.15);
        }
      `}</style>
    </div>
  );
}

export default TextBox;

